/**
 * 应用统一错误模型
 *
 * @typedef {'validation' | 'auth' | 'forbidden' | 'rateLimit' | 'network' | 'system' | 'unknown'} AppErrorType
 *
 * @typedef {Object} AppError
 * @property {string} name - 错误名称 'AppError'
 * @property {string} message - 错误消息
 * @property {string} friendlyMessage - 用户友好的错误消息
 * @property {AppErrorType} type - 错误类型
 * @property {number} [httpStatus] - HTTP状态码
 * @property {string} [errorCode] - 后端错误代码
 * @property {string} [operation] - 操作名称
 * @property {string} [url] - 请求URL
 * @property {any} [raw] - 原始错误对象
 */

/**
 * HTTP状态码默认消息映射
 */
const DEFAULT_MESSAGES = {
  400: '请求参数错误',
  401: '未授权，请登录',
  403: '没有权限访问该资源',
  404: '请求的资源不存在',
  422: '请求验证失败',
  429: '请求过于频繁，请稍后重试',
  500: '服务器内部错误',
  502: '网关错误',
  503: '服务暂时不可用',
  504: '网关超时'
}

/**
 * 根据HTTP状态码获取默认错误消息
 * @param {number} status - HTTP状态码
 * @returns {string} 默认错误消息
 */
export function getDefaultMessage(status) {
  if (!status) {
    return '网络连接失败，请检查网络'
  }
  return DEFAULT_MESSAGES[status] || `请求失败 (${status})`
}

/**
 * 从axios错误构建标准AppError
 * @param {any} error - axios错误对象
 * @param {string} [operation] - 操作名称
 * @returns {AppError} 标准化的AppError对象
 */
export function buildAppError(error, operation = '') {
  const { response, config, message: axiosMessage } = error || {}
  const { status, data } = response || {}

  // 提取后端返回的错误消息
  const backendMessage =
    data?.message ||
    data?.msg ||
    data?.error ||
    null

  const friendlyMessage = backendMessage || getDefaultMessage(status) || axiosMessage || '操作失败，请重试'

  // 创建AppError对象
  const appError = new Error(friendlyMessage)
  appError.name = 'AppError'
  appError.friendlyMessage = friendlyMessage
  appError.httpStatus = status
  appError.errorCode = data?.code || data?.errorCode
  appError.operation = operation
  appError.url = config?.url
  appError.raw = error

  // 根据状态码和错误代码判断错误类型
  if (!status) {
    // 网络错误：无响应
    appError.type = 'network'
  } else if (status === 400 || status === 422) {
    // 参数验证错误
    appError.type = 'validation'
  } else if (status === 401) {
    // 认证错误
    appError.type = 'auth'
  } else if (status === 403) {
    // 权限错误
    appError.type = 'forbidden'
  } else if (status === 429) {
    // 限流错误
    appError.type = 'rateLimit'
  } else if (status >= 500) {
    // 服务器错误
    appError.type = 'system'
  } else {
    // 未知错误
    appError.type = 'unknown'
  }

  // 特殊处理：根据errorCode进一步细化类型
  if (data?.code) {
    const code = data.code.toUpperCase()

    // 验证码相关错误归类为validation
    if (code.includes('VERIFICATION') || code.includes('CODE_EXPIRED')) {
      appError.type = 'validation'
    }

    // 限流相关错误
    if (code.includes('RATE_LIMIT') || code.includes('TOO_MANY')) {
      appError.type = 'rateLimit'
    }

    // 余额不足等扣费错误
    if (code.includes('INSUFFICIENT') || code.includes('BALANCE')) {
      appError.type = 'validation'
      appError.subType = 'billing'
    }
  }

  return appError
}

/**
 * 包装未知错误为AppError
 * @param {any} error - 任意错误对象
 * @returns {AppError} AppError对象
 */
export function wrapUnknownError(error) {
  // 如果已经是AppError，直接返回
  if (error?.name === 'AppError') {
    return error
  }

  const message = error?.message || error?.toString() || '操作失败，请重试'

  const appError = new Error(message)
  appError.name = 'AppError'
  appError.friendlyMessage = message
  appError.type = 'unknown'
  appError.raw = error

  return appError
}

/**
 * 判断是否为AppError
 * @param {any} error - 错误对象
 * @returns {boolean}
 */
export function isAppError(error) {
  return error?.name === 'AppError'
}
